import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthService } from '@core/auth/auth.service';
import { BrokerProfileService } from '@core/services/auth/broker-profile/broker-profile.service';
import { Profile } from '@core/services/auth/profile/profile.interface';
import {
    BrokerProfileOption,
    TwoStepAuthOption,
    SwitchOptionsResponse
} from '@core/auth/auth.model';

@Component({
    standalone: true,
    selector: 'profile-switch-dialog',
    templateUrl: './profile-switch-dialog.component.html',
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        TranslocoModule
    ]
})
export class ProfileSwitchDialogComponent implements OnInit {
    loading: boolean = true;
    errorMessage: string | null = null;
    entityType: string = '';
    currentStep: 1 | 2 = 1;

    // Broker options
    brokerOptions: BrokerProfileOption[] = [];

    // Two-step options (contributor / sub-contributor)
    twoStepOptions: TwoStepAuthOption[] = [];
    posOptions: TwoStepAuthOption[] = [];

    // Selected step 1 context
    private _selectedCompanyUuid: string | null = null;
    private _selectedContributorUuid: string | null = null;

    constructor(
        private _dialogRef: MatDialogRef<ProfileSwitchDialogComponent>,
        private _authService: AuthService,
        private _brokerProfileService: BrokerProfileService
    ) {}

    ngOnInit(): void {
        this.loadOptions();
    }

    getStepTitle(): string {
        if (this.currentStep === 2) {
            return 'user.switch_profile.step2_pos';
        }
        switch (this.entityType) {
            case 'BROKER_POINT_OF_SALE':
                return 'user.switch_profile.step1_broker';
            case 'CONTRIBUTOR':
                return 'user.switch_profile.step1_contributor';
            case 'SUB_CONTRIBUTOR':
                return 'user.switch_profile.step1_sub_contributor';
            default:
                return 'user.switch_profile.dialog_title';
        }
    }

    getVisibleOptions(): any[] {
        if (this.entityType === 'BROKER_POINT_OF_SALE') {
            return this.brokerOptions;
        }
        if (this.currentStep === 2) {
            return this.posOptions;
        }
        return this.twoStepOptions;
    }

    // ---- Broker ----

    selectBrokerProfile(profile: BrokerProfileOption): void {
        this.loading = true;
        this._authService.switchBrokerProfile(profile.profileUuid).subscribe({
            next: () => {
                this._brokerProfileService.selectProfile(new Profile({
                    id: profile.profileUuid,
                    name: profile.profileName,
                    managementEntity: profile.companyUuid,
                    permissions: [],
                    level: '',
                    description: ''
                }));
                this._dialogRef.close(true);
                window.location.href = '/home';
            },
            error: (error) => this.handleError(error)
        });
    }

    // ---- Step 1: Contributor / Sub-Contributor ----

    selectStep1Option(option: TwoStepAuthOption): void {
        if (this.entityType === 'CONTRIBUTOR') {
            this.handleContributorStep1(option);
        } else if (this.entityType === 'SUB_CONTRIBUTOR') {
            this.handleSubContributorStep1(option);
        }
    }

    // ---- Step 2: POS selection ----

    selectStep2Option(option: TwoStepAuthOption): void {
        if (this.entityType === 'CONTRIBUTOR') {
            this.handleContributorStep2(option);
        } else if (this.entityType === 'SUB_CONTRIBUTOR') {
            this.handleSubContributorStep2(option);
        }
    }

    goBack(): void {
        this.currentStep = 1;
        this.posOptions = [];
        this._selectedCompanyUuid = null;
        this._selectedContributorUuid = null;
    }

    // ---- Private ----

    private loadOptions(): void {
        this.loading = true;
        this._authService.getSwitchOptions().subscribe({
            next: (response: SwitchOptionsResponse) => {
                this.entityType = response.entityType;

                if (this.entityType === 'BROKER_POINT_OF_SALE') {
                    this.brokerOptions = response.options as BrokerProfileOption[];
                } else {
                    this.twoStepOptions = response.options as TwoStepAuthOption[];
                }

                this.loading = false;
            },
            error: (error) => this.handleError(error)
        });
    }

    private handleContributorStep1(option: TwoStepAuthOption): void {
        // CAS ADMIN: option directe
        if (option.optionType === 'ADMIN') {
            this.loading = true;
            this._authService.switchContributorProfile(option.optionUuid, option.optionUuid).subscribe({
                next: () => {
                    this.storeSelectedProfile(option);
                    this._dialogRef.close(true);
                    window.location.href = '/home';
                },
                error: (error) => this.handleError(error)
            });
            return;
        }

        // CAS COMPAGNIE: charger les POS
        this.loading = true;
        this._selectedCompanyUuid = option.optionUuid;
        this._authService.getContributorPointsOfSale(option.optionUuid).subscribe({
            next: (posOptions: TwoStepAuthOption[]) => {
                this.posOptions = posOptions;
                this.currentStep = 2;
                this.loading = false;

                // Auto-select si une seule option
                if (posOptions.length === 1) {
                    this.handleContributorStep2(posOptions[0]);
                }
            },
            error: (error) => this.handleError(error)
        });
    }

    private handleContributorStep2(option: TwoStepAuthOption): void {
        if (!this._selectedCompanyUuid) return;

        this.loading = true;
        this._authService.switchContributorProfile(this._selectedCompanyUuid, option.optionUuid).subscribe({
            next: () => {
                this.storeSelectedProfile(option);
                this._dialogRef.close(true);
                window.location.href = '/home';
            },
            error: (error) => this.handleError(error)
        });
    }

    private handleSubContributorStep1(option: TwoStepAuthOption): void {
        this.loading = true;
        this._selectedContributorUuid = option.optionUuid;
        this._authService.getSubContributorPointsOfSale(option.optionUuid).subscribe({
            next: (posOptions: TwoStepAuthOption[]) => {
                this.posOptions = posOptions;
                this.currentStep = 2;
                this.loading = false;

                // Auto-select si une seule option
                if (posOptions.length === 1) {
                    this.handleSubContributorStep2(posOptions[0]);
                }
            },
            error: (error) => this.handleError(error)
        });
    }

    private handleSubContributorStep2(option: TwoStepAuthOption): void {
        if (!this._selectedContributorUuid) return;

        this.loading = true;
        this._authService.switchSubContributorProfile(this._selectedContributorUuid, option.optionUuid).subscribe({
            next: () => {
                this.storeSelectedProfile(option);
                this._dialogRef.close(true);
                window.location.href = '/home';
            },
            error: (error) => this.handleError(error)
        });
    }

    private storeSelectedProfile(option: TwoStepAuthOption): void {
        this._brokerProfileService.selectProfile(new Profile({
            id: option.optionUuid,
            name: option.optionName,
            managementEntity: option.parentEntityUuid || '',
            permissions: [],
            level: '',
            description: ''
        }));
    }

    private handleError(error: any): void {
        console.error('Profile switch error:', error);
        this.loading = false;
        this.errorMessage = 'user.switch_profile.error';
    }
}
